import type { Bonus, Buyer, Faq, Stage, Testimonial, Volume } from '../types/content';

export const images = {
  doctor: 'https://cdn.magicpatterns.com/uploads/cKS5M9JwRz67tFLiBNPoNU/VALERIA_MONTES_-_BIOMECANICA.png',
  mockup: 'https://cdn.magicpatterns.com/uploads/tGu63Kw5NGi2MXgD7TeEUA/MOCKUP_DENTALIMPLANT-EN.png',
};

export const heroIncludes = [
  '6 specialized clinical reference volumes',
  '5 practical implantology planning resources',
  'Visual decision pathways and assessment frameworks',
  'Anatomical reference guides and planning checklists',
  'Surgical and restorative planning considerations',
  'Complication-awareness and follow-up frameworks',
  'Immediate access to the digital collection',
];

export const planningQuestions = [
  'Is this patient an appropriate candidate?',
  'What medical and dental factors require additional assessment?',
  'What does the CBCT reveal about the available anatomy?',
  'How does the intended restoration influence implant planning?',
  'Are there limitations involving bone volume or soft tissue?',
  'What complications should be considered?',
  'How will the surgical and restorative phases connect?',
  'What should be evaluated during follow-up?',
];

export const stages: Stage[] = [
  { verb: 'Assess', text: 'Review the patient’s medical history, dental findings, risk factors, and treatment expectations.' },
  { verb: 'Analyze', text: 'Organize the relevant clinical and radiographic information, including CBCT findings when indicated.' },
  { verb: 'Define', text: 'Clarify the intended restorative outcome and the objectives that guide treatment planning.' },
  { verb: 'Evaluate', text: 'Identify anatomical limitations, bone and soft-tissue considerations, and factors requiring specialist assessment.' },
  { verb: 'Plan', text: 'Bring the relevant surgical and prosthetic considerations into a structured treatment proposal.' },
  { verb: 'Review', text: 'Use a pre-treatment review framework to identify unanswered questions, limitations, and potential complications.' },
  { verb: 'Follow Up', text: 'Understand the importance of documenting treatment progress, restorative outcomes, and long-term maintenance.' },
];

export const beforeItems = [
  'You review the patient’s radiographs without a consistent framework for organizing the findings.',
  'You move between surgical and restorative considerations without clearly documenting how they connect.',
  'You spend time searching through textbooks, screenshots, notes, and clinical references.',
  'You understand individual implantology concepts but find it difficult to organize them into a coherent treatment sequence.',
  'You sometimes identify unanswered planning questions only after reviewing the case several times.',
  'You have no consistent educational template for documenting your reasoning.',
  'Every complex case feels like another planning process you have to rebuild.',
];

export const afterItems = [
  'You begin with an organized patient assessment.',
  'You use a visual framework to review relevant anatomical findings.',
  'You connect restorative objectives with surgical planning considerations.',
  'You document factors involving bone, soft tissue, and treatment limitations.',
  'You identify questions that require additional assessment before proceeding.',
  'You understand how the surgical, restorative, and maintenance stages relate.',
  'You create a reusable case-planning document.',
];

export const familiarItems = [
  {
    title: 'You understand implantology concepts, but connecting them is challenging.',
    body: ['You know the importance of anatomy, imaging, osseointegration, restorative planning, and maintenance.', 'But organizing everything into one coherent process can take considerable effort.'],
  },
  {
    title: 'You want a more structured way to review CBCT findings.',
    body: ['You understand that anatomical interpretation is critical, but you would benefit from a visual reference that helps you organize the relevant observations.'],
  },
  {
    title: 'You want to better understand the relationship between surgical and prosthetic planning.',
    body: ['You recognize that implant treatment must consider the intended restoration, but you want a clearer educational framework for connecting these decisions.'],
  },
  {
    title: 'You sometimes feel overwhelmed by complex implant cases.',
    body: ['Multiple anatomical, biological, and restorative factors need to be considered together.', 'You want a systematic way to organize those considerations.'],
  },
  {
    title: 'You want to recognize potential planning limitations earlier.',
    body: ['You understand the importance of evaluating risk factors, anatomical constraints, and potential complications before treatment.'],
  },
  {
    title: 'You want a reference you can return to repeatedly.',
    body: ['Not another collection of disconnected theory.', 'A practical educational resource with visual maps, checklists, and structured planning documents.'],
  },
];

export const subjects = [
  'Implant anatomy',
  'CBCT interpretation',
  'Osseointegration',
  'Bone regeneration',
  'Surgical principles',
  'Prosthetic rehabilitation',
  'Complication management',
  'Peri-implant maintenance',
];

export const volumes: Volume[] = [
  {
    number: 'I',
    title: 'The Implant Patient Assessment Blueprint',
    tagline: 'Understand the Patient Before Planning the Implant.',
    intro: [
      'Every implant treatment begins with a fundamental question: What needs to be evaluated before deciding whether implant treatment is appropriate?',
      'This volume organizes the essential components of the initial assessment.',
    ],
    bullets: [
      'Medical and dental history considerations',
      'Patient expectations and treatment objectives',
      'Systemic and local risk factors',
      'Periodontal and oral health assessment',
      'Clinical examination frameworks',
      'Factors affecting treatment suitability',
      'When additional evaluation or referral may be necessary',
      'Structured patient assessment documentation',
    ],
    extra: 'You’ll also find educational assessment matrices designed to help you organize the information collected during the initial consultation.',
    result: 'A clearer framework for documenting patient-related considerations before moving into implant planning.',
  },
  {
    number: 'II',
    title: 'CBCT Anatomy & Implant Imaging Roadmap',
    tagline: 'Understand the Anatomical Findings That Influence Implant Planning.',
    intro: [
      'A CBCT scan contains valuable three-dimensional information. But obtaining an image is not the same as interpreting it appropriately.',
      'This volume introduces a structured educational approach to reviewing anatomical considerations relevant to implantology.',
    ],
    bullets: [
      'Maxillary and mandibular anatomical landmarks',
      'Relevant neurovascular structures',
      'Maxillary sinus considerations',
      'Alveolar ridge anatomy',
      'Bone morphology and anatomical limitations',
      'Three-dimensional imaging orientation',
      'Common imaging interpretation challenges',
      'Documentation of relevant anatomical observations',
    ],
    extra: 'The volume also addresses the limitations of imaging and the importance of professional interpretation.',
    result: 'A more organized way to study the anatomical information relevant to implant treatment planning.',
  },
  {
    number: 'III',
    title: 'Prosthetically Driven Implant Planning Blueprint',
    tagline: 'Understand Why Implant Planning Begins With the Intended Restoration.',
    intro: [
      'One of the most important concepts in contemporary implantology is the relationship between implant positioning and the planned prosthetic outcome.',
      'This volume focuses on understanding that connection.',
    ],
    bullets: [
      'Restorative objectives in implant planning',
      'Single-tooth and multiple-tooth rehabilitation considerations',
      'Prosthetic space assessment',
      'Implant positioning concepts',
      'Emergence profile considerations',
      'Occlusal factors',
      'Restorative component considerations',
      'Communication between surgical and restorative teams',
    ],
    extra: 'You’ll study how restorative requirements can influence the broader planning process.',
    result: 'A clearer understanding of how prosthetic objectives and surgical considerations fit into the same treatment plan.',
  },
  {
    number: 'IV',
    title: 'Bone & Soft Tissue Assessment Clinical Pathway',
    tagline: 'Recognize the Biological and Anatomical Factors That Shape Implant Treatment.',
    intro: [
      'Not every implant site presents the same anatomical conditions. Available bone, soft-tissue characteristics, and local biological factors can influence the complexity of treatment.',
      'This volume provides an educational framework for evaluating these considerations.',
    ],
    bullets: [
      'Alveolar ridge morphology',
      'Bone volume and anatomical limitations',
      'Soft-tissue characteristics',
      'Peri-implant tissue considerations',
      'Common categories of ridge defects',
      'Principles of guided bone regeneration',
      'Factors influencing treatment complexity',
      'Situations requiring advanced assessment or specialist expertise',
    ],
    extra: 'Rather than presenting one approach as appropriate for every patient, the material emphasizes patient-specific evaluation and the limitations of generalized planning frameworks.',
    result: 'A structured reference for understanding the biological and anatomical considerations involved in implant planning.',
  },
  {
    number: 'V',
    title: 'Implant Complications & Safety Blueprint',
    tagline: 'Learn to Recognize Risk Factors and Potential Complications.',
    intro: [
      'Some of the most important decisions in implantology happen before treatment begins. Understanding potential complications is an essential part of clinical education.',
      'This volume organizes the major categories of implant-related complications and the factors associated with them.',
    ],
    bullets: [
      'Anatomical risk considerations',
      'Biological complications',
      'Surgical complications',
      'Prosthetic and mechanical complications',
      'Early and late implant-related problems',
      'Peri-implant disease considerations',
      'Warning signs requiring further evaluation',
      'Clinical documentation and reassessment principles',
    ],
    extra: 'You’ll also find educational case-review questions to help strengthen your understanding of implant-related risks.',
    result: 'A clearer reference for recognizing potential complications and understanding when additional assessment is important.',
  },
];

export const finalVolume = {
  number: 'VI',
  title: 'The Complete Implant Treatment Roadmap',
  tagline: 'Connect Assessment, Planning, Restoration, and Follow-Up.',
  intro: [
    'Now it’s time to bring the information together. This final volume introduces the complete Dental Implant Clinical Roadmap framework.',
    'You’ll learn how to organize a case into a structured visual document that brings the major planning considerations into one place.',
  ],
  roadmap: [
    { title: 'Patient Assessment', text: 'Relevant medical, dental, and patient-specific information.' },
    { title: 'Clinical Findings', text: 'Important examination observations and diagnostic information.' },
    { title: 'Anatomical Evaluation', text: 'Relevant imaging findings and anatomical limitations.' },
    { title: 'Restorative Objectives', text: 'The intended prosthetic outcome and related considerations.' },
    { title: 'Surgical Planning Considerations', text: 'Factors that influence treatment planning and specialist assessment.' },
    { title: 'Risk Review', text: 'Potential complications, limitations, and unresolved questions.' },
    { title: 'Treatment Sequence', text: 'A structured overview of the proposed phases, subject to professional judgment.' },
    { title: 'Follow-Up & Maintenance', text: 'Documentation, reassessment, and long-term maintenance considerations.' },
  ],
  goal: 'To organize the major components of an implant case into one coherent educational planning framework.',
};

export const resources = [
  { title: 'Implant Patient Assessment Checklists', text: 'Structured checklists covering important patient evaluation considerations.' },
  { title: 'Anatomical Reference Guides', text: 'Visual educational references illustrating structures and anatomical considerations relevant to implant planning.' },
  { title: 'Clinical Decision Matrices', text: 'Frameworks for organizing findings, treatment objectives, limitations, and questions requiring further assessment.' },
  { title: 'Implant Treatment Planning Worksheets', text: 'Reusable templates for documenting the major stages of implant treatment planning.' },
  { title: 'Follow-Up Documentation Tools', text: 'Educational worksheets for organizing treatment progress, reassessment, and maintenance considerations.' },
];

export const bonuses: Bonus[] = [
  {
    number: 1,
    title: 'Implant Anatomy Visual Reference Atlas™',
    value: 37,
    image: 'https://cdn.magicpatterns.com/uploads/kha2qQsCweqEsRuMKTA6mb/BONUS_1_DDDDENTALIMPLANT-EN.png',
    intro: [
      'Implant treatment planning requires a solid understanding of relevant anatomical structures.',
      'This visual atlas brings together illustrated anatomical references for the maxilla and mandible.',
    ],
    listLabel: 'Inside',
    bullets: ['Maxillary anatomical landmarks', 'Mandibular anatomical landmarks', 'Neurovascular structures', 'Maxillary sinus anatomy', 'Alveolar ridge illustrations', 'Common anatomical variations', 'Visual orientation references'],
    why: 'You’ll have a dedicated educational anatomy reference to consult while studying implant planning concepts.',
  },
  {
    number: 2,
    title: '25 Implant Planning Case Studies™',
    value: 37,
    image: 'https://cdn.magicpatterns.com/uploads/fGisbE62dxZwBnBBfy8ttV/BONUS_2_DENTALIMPLANT-EN.png',
    intro: [
      'Understanding concepts is important. Learning to connect them through case-based reasoning is equally valuable.',
      'This collection presents 25 educational implant-planning scenarios designed to develop structured clinical reasoning.',
    ],
    listLabel: 'Each case includes',
    bullets: ['Patient presentation', 'Relevant clinical information', 'Imaging considerations', 'Restorative objectives', 'Anatomical limitations', 'Questions for clinical reasoning', 'Discussion of planning considerations'],
    note: 'Cases may be fictionalized or educational composites and will be identified accordingly.',
    why: 'You’ll be able to practice connecting different implantology concepts through structured case discussions.',
  },
  {
    number: 3,
    title: 'The Implant Pre-Treatment Safety Audit™',
    value: 29,
    image: 'https://cdn.magicpatterns.com/uploads/9dd2jqoDgyiGsDbr9JNQZS/BONUS_3_DENTALIMPLANT-EN.png',
    intro: [
      'Before progressing from planning to treatment, important questions should be reviewed.',
      'This resource provides a structured educational checklist covering the major categories of pre-treatment considerations.',
    ],
    listLabel: 'Inside',
    bullets: ['Patient-related risk review', 'Anatomical assessment considerations', 'Restorative planning questions', 'Bone and soft-tissue considerations', 'Documentation review', 'Potential complication awareness', 'Unresolved planning questions', 'Referral and specialist assessment considerations'],
    why: 'You’ll have a repeatable framework for reviewing important planning considerations before treatment decisions are finalized.',
  },
  {
    number: 4,
    title: 'Implant Complications Visual Casebook™',
    value: 27,
    image: 'https://cdn.magicpatterns.com/uploads/57uoGaQrUqePD8xVrTukJT/BONUS_4_DENTALIMPLANT-EN.png',
    intro: [
      'Some implant-related problems are easier to understand when presented visually.',
      'This casebook organizes common complication categories through illustrations, educational scenarios, and clinical discussion.',
    ],
    listLabel: 'Inside',
    bullets: ['Biological complication scenarios', 'Anatomical risk illustrations', 'Mechanical and prosthetic complications', 'Peri-implant tissue problems', 'Risk-factor discussions', 'Warning-sign reference charts', 'Questions for case review'],
    why: 'You’ll be able to study potential complications through visual references instead of relying exclusively on descriptive text.',
  },
  {
    number: 5,
    title: 'The One-Page Implant Treatment Planning Template™',
    value: 27,
    image: 'https://cdn.magicpatterns.com/uploads/tg8MVaFzvm8HzmC8ts4eXP/BONUS_5_DENTALIMPLANT-EN.png',
    intro: [
      'Imagine having the major components of an implant case organized in one document. That’s the purpose of this template.',
    ],
    listLabel: 'Dedicated sections for',
    bullets: ['Patient assessment', 'Clinical findings', 'Imaging observations', 'Restorative objectives', 'Anatomical considerations', 'Risk factors', 'Treatment phases', 'Reassessment and follow-up'],
    note: 'You can duplicate the template for educational case reviews and adapt it to your documentation workflow.',
    why: 'Instead of organizing every case from a blank page, you’ll have a reusable visual planning framework.',
  },
];

export const offerIncludes = [
  'All 6 specialized volumes',
  'All 5 exclusive bonuses',
  'Digital planning resources',
  'Lifetime access to the purchased materials',
  'No recurring subscription',
  'Immediate digital access',
  '7-day refund guarantee',
];

export const guaranteeItems = [
  'Explore the six specialized volumes.',
  'Review the clinical planning frameworks.',
  'Study the visual anatomy references.',
  'Examine the educational case studies.',
  'Test the planning worksheets.',
  'Evaluate how the resources fit your learning needs.',
];

export const testimonials: Testimonial[] = [
  {
    name: 'Dr. Alejandro Ramírez',
    role: 'General Dentist',
    country: 'Mexico',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/a885795c-4f29-4970-b39e-c66d8b68d317.jpg',
    headline: 'A clearer way to organize implant planning.',
    quote: 'What I value most is having patient assessment, imaging, surgical considerations, and restoration organized within one framework. That makes the learning process so much easier to follow.',
  },
  {
    name: 'Dr. Camila Torres',
    role: 'Restorative Dentist',
    country: 'Colombia',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/7bcef52f-7e8c-4832-be28-ae5ce2ba2190.jpg',
    headline: 'The anatomical references are particularly useful.',
    quote: 'Having a visual guide that connects CBCT anatomy with implant-planning considerations has become my go-to reference when reviewing educational cases.',
  },
  {
    name: 'Dr. Ricardo Méndez',
    role: 'Oral Surgeon',
    country: 'Spain',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/78068d9e-3a2a-442e-9a37-e0f37968ed78.jpg',
    headline: 'I appreciate the connection between surgical and restorative planning.',
    quote: 'Implant treatment involves more than one clinical stage. Having a framework that connects the intended restoration with the broader treatment plan makes the subject easier to study.',
  },
  {
    name: 'Dr. Laura Gómez',
    role: 'Dentist',
    country: 'Argentina',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/feea1d79-a55d-480f-bd52-bf6f05e59292.jpg',
    headline: 'The complication-awareness section stands out.',
    quote: 'I especially value how it explains implant-related risks and highlights the questions professionals should consider during planning. It changed how I review my cases.',
  },
  {
    name: 'Dr. Diego Herrera',
    role: 'Prosthodontist',
    country: 'Chile',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/95c02c50-aa4f-4c26-a2b8-bd1f4ac96b25.jpg',
    headline: 'The one-page roadmap is a practical idea.',
    quote: 'A reusable template for organizing patient findings, anatomical considerations, treatment objectives, and follow-up information made my case reviews much more systematic.',
  },
  {
    name: 'Dr. Sofía Ramírez',
    role: 'Implantologist',
    country: 'Peru',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/d36baa9a-a970-409f-91a3-2d8425c4b9cf.jpg',
    headline: 'Finally, every stage in one place.',
    quote: 'The 7-stage roadmap gave me a repeatable way to structure my thinking from the first consultation to follow-up. For $27, it is one of the best references in my library.',
  },
];

export const messageSubjects = ['Anatomy.', 'Imaging.', 'Bone biology.', 'Surgical principles.', 'Restorative planning.', 'Complications.', 'Maintenance.'];

export const messageSteps = [
  'First, understand the patient.',
  'Then organize the clinical findings.',
  'Review the relevant anatomy.',
  'Define the restorative objectives.',
  'Evaluate biological and anatomical limitations.',
  'Understand the surgical and prosthetic considerations.',
  'And finally, recognize the importance of reassessment and long-term maintenance.',
];

export const faqs: Faq[] = [
  { q: 'How will I receive Dental Implant Clinical Roadmap™?', a: ['The collection is delivered digitally. After your payment is confirmed, you’ll receive access to the six volumes and five bonuses through the delivery method specified at checkout.', 'You won’t need to wait for physical shipping.'] },
  { q: 'Is this a course or a digital book collection?', a: ['Dental Implant Clinical Roadmap™ is a digital educational reference collection.', 'It includes six specialized volumes, five bonuses, visual guides, checklists, and planning templates.', 'It is not a surgical training course or professional certification.'] },
  { q: 'Do I need to be an implant specialist?', a: ['The collection is intended for dentists, implantology professionals, oral surgeons, and dental professionals studying implant treatment planning.', 'Some topics require prior clinical knowledge.', 'The material does not qualify an inexperienced professional to perform implant surgery independently.'] },
  { q: 'Can I use the materials if I live outside the United States?', a: ['Yes. The educational content covers general implantology concepts relevant to international professional audiences.', 'However, clinical guidelines, professional regulations, treatment standards, and scope-of-practice requirements may vary by country.', 'Always follow the standards applicable to your location.'] },
  { q: 'Can I download the materials?', a: ['The collection is intended for digital use. Download options and supported file formats will be described in your purchase-access instructions.'] },
  { q: 'Is this a monthly subscription?', a: ['No. You pay $27 USD once.', 'There are no recurring subscription fees for the purchased collection.'] },
  { q: 'Will I receive all six volumes and five bonuses?', a: ['Yes. The complete offer includes all six specialized volumes and all five professional bonuses.'] },
  { q: 'Can I use the planning templates more than once?', a: ['Yes. The templates are designed as reusable educational planning resources.', 'You can duplicate them for your own study and permitted professional educational use.'] },
  { q: 'Does the system teach me how to perform implant surgery?', a: ['No. The collection explains clinical concepts, anatomical considerations, planning frameworks, and treatment stages.', 'It does not replace hands-on training, supervised surgical education, clinical competency assessment, or specialist guidance.'] },
  { q: 'Does this system guarantee successful implant treatment?', a: ['No. Implant treatment outcomes depend on many patient-specific, biological, surgical, restorative, and maintenance-related factors.', 'The system is designed to support professional education and organized clinical reasoning, not to guarantee treatment results.'] },
  { q: 'Do I get lifetime access?', a: ['Yes. Your one-time purchase includes lifetime access to the purchased digital collection, subject to the access terms provided at checkout.'] },
  { q: 'What if the materials aren’t right for me?', a: ['The offer includes a seven-day refund guarantee.', 'If you decide the collection doesn’t meet your educational needs, contact support within seven days of purchase to request a refund under the published refund policy.'] },
];

export const finalFlow = [
  'Patient Assessment',
  'Clinical Findings',
  'CBCT & Anatomical Evaluation',
  'Restorative Objectives',
  'Bone & Soft Tissue Considerations',
  'Surgical Planning Factors',
  'Complication Awareness',
  'Restoration & Follow-Up',
];

export const finalIncludes = [
  '6 specialized clinical volumes',
  '5 exclusive professional bonuses',
  'Visual anatomy references',
  'Educational planning frameworks',
  'Clinical assessment checklists',
  'Implant case-study resources',
  'Reusable planning templates',
  'Lifetime digital access',
  'One-time payment',
  '7-day refund guarantee',
];

export const buyers: Buyer[] = [
  { name: 'María Fernanda', country: 'Mexico' },
  { name: 'Carlos', country: 'Colombia' },
  { name: 'Lucía', country: 'Argentina' },
  { name: 'Javier', country: 'Spain' },
  { name: 'Valentina', country: 'Chile' },
  { name: 'Andrés', country: 'Peru' },
  { name: 'Gabriela', country: 'Ecuador' },
  { name: 'José Luis', country: 'Venezuela' },
  { name: 'Daniela', country: 'Uruguay' },
  { name: 'Miguel Ángel', country: 'Guatemala' },
  { name: 'Paola', country: 'Costa Rica' },
  { name: 'Fernando', country: 'Dominican Republic' },
  { name: 'Natalia', country: 'Bolivia' },
  { name: 'Ricardo', country: 'Paraguay' },
  { name: 'Isabel', country: 'Panama' },
  { name: 'Sebastián', country: 'Puerto Rico' },
];
